import styled from '@emotion/styled'
import theme from '../../../styles/theme'
import { InfoSmall600Style, TextXS500Style } from '../../../styles/Text.styles'
import { css } from '@emotion/react'
import { scrollbarStyle } from '../../../styles/Scrollbar.styles'

// Interface for textarea style props
interface TextareaStyleProps {
  isEditable: boolean
  isFocused: boolean
  isScrollMode: boolean
  isScrolledFromTop: boolean
}

// Interface for markdown container style props
interface MarkdownContainerStyleProps {
  isScrollMode: boolean
  isScrolledFromTop: boolean
}

// Interface for GradientContainer props
interface GradientContainerProps {
  show: boolean
}

export const textareaStyle = ({
  isEditable,
  isFocused,
  isScrollMode,
  isScrolledFromTop,
}: TextareaStyleProps) => css`
  ${TextXS500Style}
  color: ${theme.colors.veryVeryFadedText};
  width: 100%;
  resize: none;
  padding: 0.4rem;
  line-height: 1.5;
  border-radius: 0.4rem;
  box-sizing: border-box;

  height: 100%;
  border: none;
  outline: none;
  background-color: ${isEditable ? '#ffffff' : 'transparent'};

  ${scrollbarStyle}

  // Show outline when editable and focused
    ${isEditable &&
  isFocused &&
  `
        border-color: #66afe9;
        outline: auto;
        box-shadow: 0 0 0 2px rgba(102, 175, 233, 0.25);
    `}

    // Show top border when scrolled from top in scroll mode or edit mode
    ${(isScrollMode || isEditable) &&
  isScrolledFromTop &&
  `
        border-top: 2px solid #ddd;
    `}
`

export const markdownContainerStyle = ({
  isScrollMode,
  isScrolledFromTop,
}: MarkdownContainerStyleProps) => css`
  ${TextXS500Style}
  color: ${theme.colors.veryVeryFadedText};
  width: 100%;
  height: 100%;
  padding: 0.4rem;
  line-height: 1.5;
  border-radius: 0.4rem;
  box-sizing: border-box;
  background-color: transparent;
  word-break: break-word;
  ${scrollbarStyle}

  // Show top border when scrolled from top in scroll mode
    ${isScrollMode &&
  isScrolledFromTop &&
  `
        border-top: 2px solid #ddd;
    `}

    /* Adjust markdown styles */
    p {
    margin: 0.5rem 0;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-top: 1rem;
    margin-bottom: 0.5rem;
  }

  ul,
  ol {
    padding-left: 1.5rem;
  }

  code {
    background-color: rgba(0, 0, 0, 0.05);
    padding: 0.1rem 0.3rem;
    border-radius: 3px;
  }

  pre {
    background-color: rgba(0, 0, 0, 0.05);
    padding: 0.5rem;
    border-radius: 4px;
    overflow-x: auto;
  }

  blockquote {
    border-left: 3px solid #ddd;
    margin-left: 0;
    padding-left: 1rem;
    color: rgba(0, 0, 0, 0.6);
  }
`

export const OuterContainer = styled.div`
  background-color: ${theme.colors.surface};
  position: relative;
  width: 100%; // Allow container to grow and fill available space
  box-sizing: border-box;
`

export const Container = styled.div`
  position: relative;
`

export const GradientContainer = styled.div<GradientContainerProps>`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 2.7rem;
  background: linear-gradient(
    rgba(245, 245, 245, 0) 0%,
    #ffffff88 50%,
    #ffffffc7 100%
  );
  backdrop-filter: blur(0.05rem);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  opacity: 0;
  visibility: hidden;
  transition:
    opacity 0.3s ease,
    visibility 0.3s ease;

  ${(props) =>
    props.show &&
    `
        opacity: 1;
        visibility: visible;
    `}
`

export const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  border-bottom-width: 2px;
  border-bottom-style: dotted;
  border-bottom-color: ${theme.colors.backgroundDark};
  padding-bottom: 0.5rem;
`

export const HeaderTitle = styled.span`
  ${InfoSmall600Style};
  color: ${theme.colors.veryVeryVeryFadedText};
  margin-left: 0.5rem;
`

export const Toolbox = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  position: relative;
  z-index: 10; // Higher than Plotly's modebar which is typically 999
  > * {
    cursor: pointer;
  }
`

export const ContentArea = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  overflow: hidden;

  min-height: 1rem;
  border-radius: 0.4rem;
`

export const GraphPlotArea = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  min-height: 20rem;
  max-height: 35rem;
  padding-bottom: 1rem;
  border-bottom-width: 2px;
  border-bottom-style: dotted;
  border-bottom-color: ${theme.colors.backgroundDark};
`

export const Footer = styled.div`
  margin-top: 1rem;
  color: ${theme.colors.veryFadedText};
  font-size: 0.85rem;
`

export const ActionContainer = styled.div`
  display: flex;
  gap: 1rem;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  align-items: center;
  justify-content: flex-end;
`
